import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, AlertTriangle, XCircle, Zap, Filter } from 'lucide-react';

export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface SeverityFilterProps {
  onFiltersChange: (filters: SeverityLevel[]) => void;
  className?: string;
  isSticky?: boolean;
}

const SEVERITY_CONFIG = {
  critical: {
    label: 'Critical',
    icon: Zap,
    color: 'bg-red-600 hover:bg-red-700 text-white',
    selectedColor: 'bg-red-700 text-white ring-2 ring-red-400',
    count: 0,
  },
  high: {
    label: 'High',
    icon: XCircle,
    color: 'bg-red-500 hover:bg-red-600 text-white',
    selectedColor: 'bg-red-600 text-white ring-2 ring-red-300',
    count: 0,
  },
  medium: {
    label: 'Medium',
    icon: AlertTriangle,
    color: 'bg-yellow-500 hover:bg-yellow-600 text-white',
    selectedColor: 'bg-yellow-600 text-white ring-2 ring-yellow-300',
    count: 0,
  },
  low: {
    label: 'Low',
    icon: AlertCircle,
    color: 'bg-blue-500 hover:bg-blue-600 text-white',
    selectedColor: 'bg-blue-600 text-white ring-2 ring-blue-300',
    count: 0,
  },
  info: {
    label: 'Info',
    icon: AlertCircle,
    color: 'bg-gray-500 hover:bg-gray-600 text-white',
    selectedColor: 'bg-gray-600 text-white ring-2 ring-gray-300',
    count: 0,
  },
} as const;

export const SeverityFilter: React.FC<SeverityFilterProps> = ({ 
  onFiltersChange, 
  className = '',
  isSticky = false
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get selected filters from URL or default to all
  const selectedFilters: SeverityLevel[] = React.useMemo(() => {
    const filters = searchParams.get('severity');
    if (filters) {
      return filters.split(',').filter(f => 
        Object.keys(SEVERITY_CONFIG).includes(f)
      ) as SeverityLevel[];
    }
    return Object.keys(SEVERITY_CONFIG) as SeverityLevel[];
  }, [searchParams]);

  const toggleFilter = (severity: SeverityLevel) => {
    const newFilters = selectedFilters.includes(severity)
      ? selectedFilters.filter(f => f !== severity)
      : [...selectedFilters, severity];
    
    // Update URL
    const newSearchParams = new URLSearchParams(searchParams);
    if (newFilters.length === Object.keys(SEVERITY_CONFIG).length) {
      // All selected, remove param
      newSearchParams.delete('severity');
    } else {
      newSearchParams.set('severity', newFilters.join(','));
    }
    setSearchParams(newSearchParams);
    
    // Notify parent
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('severity');
    setSearchParams(newSearchParams);
    onFiltersChange([]);
  };

  const selectAllFilters = () => {
    const allFilters = Object.keys(SEVERITY_CONFIG) as SeverityLevel[];
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('severity');
    setSearchParams(newSearchParams);
    onFiltersChange(allFilters);
  };

  const baseClasses = "flex flex-wrap items-center gap-3 p-4 bg-gray-800/95 backdrop-blur-sm border-b border-gray-700";
  const stickyClasses = isSticky ? "sticky top-0 z-40" : "";
  const combinedClasses = `${baseClasses} ${stickyClasses} ${className}`;

  return (
    <div className={combinedClasses} role="toolbar" aria-label="Severity filter toolbar">
      <div className="flex items-center gap-2 text-sm text-gray-300">
        <Filter className="w-4 h-4" />
        <span className="font-medium">Filter by severity:</span>
      </div>
      
      <div className="flex flex-wrap items-center gap-2">
        {(Object.keys(SEVERITY_CONFIG) as SeverityLevel[]).map((severity) => {
          const config = SEVERITY_CONFIG[severity];
          const Icon = config.icon;
          const isSelected = selectedFilters.includes(severity);
          
          return (
            <button
              key={severity}
              onClick={() => toggleFilter(severity)}
              className={`
                inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium 
                transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800
                hover:scale-105 transform
                ${isSelected ? config.selectedColor : `${config.color} opacity-70 hover:opacity-90`}
              `}
              title={`${isSelected ? 'Hide' : 'Show'} ${config.label} severity issues`}
              aria-pressed={isSelected}
              role="button"
            >
              <Icon className="w-4 h-4" />
              <span>{config.label}</span>
            </button>
          );
        })}
      </div>
      
      <div className="flex items-center gap-2 ml-auto">
        <button
          onClick={selectAllFilters}
          className="text-sm text-blue-400 hover:text-blue-300 transition-colors px-2 py-1 rounded hover:bg-blue-400/10"
          disabled={selectedFilters.length === Object.keys(SEVERITY_CONFIG).length}
          aria-label="Select all severity filters"
        >
          All
        </button>
        <span className="text-gray-500">|</span>
        <button
          onClick={clearAllFilters}
          className="text-sm text-gray-400 hover:text-gray-300 transition-colors px-2 py-1 rounded hover:bg-gray-400/10"
          disabled={selectedFilters.length === 0}
          aria-label="Clear all severity filters"
        >
          Clear
        </button>
      </div>
    </div>
  );
};